import { Component, type ErrorInfo, type ReactNode } from 'react';

import { ErrorCard } from './error-card';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onReset?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * React error boundary that catches render errors and displays a fallback UI.
 * Prevents the entire app from white-screening on unexpected runtime errors.
 *
 * Accepts an optional `onReset` callback that is called alongside the internal
 * state reset when the user clicks "Try Again". This allows parent components
 * (e.g. QueryErrorBoundary) to hook in additional cleanup like resetting
 * failed TanStack Query caches.
 */
export class ErrorBoundary extends Component<Props, State> {
  private readonly env = import.meta.env.VITE_ENV;

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
    this.env = import.meta.env.VITE_ENV;
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);
  }

  private handleReset = () => {
    this.props.onReset?.();
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return (
        <div className="p-4 flex justify-center items-center h-screen">
          <ErrorCard
            title="Something went wrong"
            message={this.env === 'production' ? 'An unexpected error occurred.' : this.state.error?.message || 'An unexpected error occurred.'}
            onRetry={this.handleReset}
          />
        </div>
      );
    }

    return this.props.children;
  }
}
